import { router, useLocalSearchParams } from 'expo-router';
import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';

import { useGetNpoPaymentAccountSteps } from '@/api/hooks/paymentAccountsHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import DotsLoader from '@/components/DotsLoader';
import FormWrapper from '@/components/forms/FormWrapper';
import NumeratedBulletList from '@/components/NumeratedBulletList';
import Text from '@/components/Text';
import { NPO_ONBOARDING_STEPS } from '@/constants/enums/NpoMerchantOnboardingRoutes';
import { SIGN_IN_BUTTON_BOTTOM } from '@/constants/layout';
import { useNpoMerchantOnboardingState } from '@/contexts/npoMerchantOnboardingContext';
import { i18n } from '@/utils/i18n';
import { getNextIncompleteStep } from '@/utils/npoMerchantOnboardingWhereToRoute';

const NpoPaymentOnboardingIntro = () => {
  const { npoId, npoItem } = useLocalSearchParams<{
    npoId: string;
    npoItem: string;
  }>();

  const list = [
    i18n.t('npoPaymentOnboarding.intro.list.ein'),
    i18n.t('npoPaymentOnboarding.intro.list.startDate'),
    i18n.t('npoPaymentOnboarding.intro.list.customerServicePhone'),
    i18n.t('npoPaymentOnboarding.intro.list.address'),
    i18n.t('npoPaymentOnboarding.intro.list.website'),
    i18n.t('npoPaymentOnboarding.intro.list.ownerSSN'),
    i18n.t('npoPaymentOnboarding.intro.list.voidedCheck'),
    i18n.t('npoPaymentOnboarding.intro.list.driversLicense'),
    i18n.t('npoPaymentOnboarding.intro.list.certificationDocument'),
    i18n.t('npoPaymentOnboarding.intro.list.articlesOfIncorporation'),
    i18n.t('npoPaymentOnboarding.intro.list.bankStatements')
  ];

  const { setInitialNpoMerchantData, setNpoId } = useNpoMerchantOnboardingState();

  const { data, isPending, isFetching, dataUpdatedAt } = useGetNpoPaymentAccountSteps(npoId);

  useEffect(() => {
    if (!isFetching && data) {
      setInitialNpoMerchantData(JSON.stringify(data));
      setNpoId(npoId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  if (isPending) {
    return (
      <View style={styles.loader}>
        <DotsLoader />
      </View>
    );
  }

  return (
    <FormWrapper keyboardVerticalOffset={0}>
      <View style={styles.container}>
        <View style={styles.body}>
          <Text>{i18n.t('npoPaymentOnboarding.intro.subtitle')}</Text>
          <Text fontWeight="700" style={styles.title}>
            {i18n.t('npoPaymentOnboarding.intro.what_you_will_need')}
          </Text>
          <NumeratedBulletList list={list} />
        </View>

        <BrandedButton
          style={styles.button}
          onPress={() => {
            if (data?.result !== undefined && data?.result !== null && data.result) {
              // Finished all steps but error at creating account
              // sending it again to last step so it can change any wrong value as EIN number
              if (data?.result?.allStepsSaved && !data.result?.hasNuveiAccount) {
                NPO_ONBOARDING_STEPS.slice(0, NPO_ONBOARDING_STEPS.length - 2).forEach((it) => router.push(it.route));
              }
              // Has nuvei account already, remove it from the flow and send it to documents
              else if (data.result.allStepsSaved && data.result.hasNuveiAccount) {
                router.replace({
                  pathname: '/npoPaymentOnboarding/documents',
                  params: {
                    npoId,
                    npoItem
                  }
                });
              }
              // Normal incompleted merchant onboarding but some steps saved
              else {
                const lastCompletedIndex = NPO_ONBOARDING_STEPS.findLastIndex((step) => step.completed(data.result!));
                NPO_ONBOARDING_STEPS.slice(0, lastCompletedIndex + 1).forEach((it) => router.push(it.route));
              }
              router.navigate({
                pathname: getNextIncompleteStep(data.result),
                params: {
                  npoId,
                  npoItem
                }
              });
            }
            // Onboarding not started yet
            else {
              router.navigate({
                pathname: getNextIncompleteStep(null),
                params: {
                  npoId,
                  npoItem
                }
              });
            }
          }}>
          {i18n.t('npoPaymentOnboarding.intro.button_label')}
        </BrandedButton>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  content: {
    flex: 1
  },

  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  body: {
    flexDirection: 'column',
    gap: 25
  },

  title: {
    fontSize: 22,
    color: 'black'
  },

  button: {
    marginBottom: SIGN_IN_BUTTON_BOTTOM
  }
});

export default NpoPaymentOnboardingIntro;
